import React, { useState } from 'react';
import { useChat } from '../../../context/ChatContext';
import { updateChannel } from '../../../services/channelService';

export default function AddMemberModal({ channel, onClose, onSuccess }) {
    const { usersData } = useChat();
    const [query, setQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const existingMemberIds = new Set(
        (channel.members || []).map(m => String(m._id || m.id))
    );

    const filtered = usersData.filter(u => {
        const id = String(u._id || u.id);
        if (existingMemberIds.has(id)) return false;
        if (!query.trim()) return true;
        return (u.name || u.displayName || u.username || '').toLowerCase().includes(query.toLowerCase());
    });

    const handleConfirm = async () => {
        if (!selectedUser) return;
        setIsSubmitting(true);
        setError(null);
        try {
            const channelId = channel._id || channel.id;
            const existingIds = (channel.members || []).map(m => m._id || m.id);
            const newMemberId = selectedUser._id || selectedUser.id;
            await updateChannel(channelId, { members: [...existingIds, newMemberId] });
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Error adding member:', err);
            setError('Failed to add member. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="w-full max-w-md bg-[#12121E] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest">Add Member</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors">✕</button>
                </div>

                {/* Search */}
                <div className="px-5 pt-4">
                    <input
                        autoFocus
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setSelectedUser(null); }}
                        placeholder="Search by name..."
                        className="w-full bg-[#0C0C14] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-indigo-500/50 transition-colors placeholder:text-gray-600"
                    />
                </div>

                {/* User list */}
                <div className="px-5 py-3 max-h-64 overflow-y-auto custom-scrollbar">
                    {filtered.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-6">No users found</p>
                    ) : (
                        filtered.map(u => {
                            const uid = u._id || u.id;
                            const isSelected = selectedUser && (selectedUser._id || selectedUser.id) === uid;
                            return (
                                <div
                                    key={uid}
                                    onClick={() => setSelectedUser(u)}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all mb-1 ${
                                        isSelected
                                            ? 'bg-indigo-500/20 border border-indigo-500/40'
                                            : 'hover:bg-white/5 border border-transparent'
                                    }`}
                                >
                                    <img
                                        src={u.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'User')}`}
                                        alt={u.name}
                                        className="w-8 h-8 rounded-lg object-cover border border-white/10"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-white truncate">{u.name || u.displayName || u.username}</p>
                                        {u.email && <p className="text-[10px] text-gray-500 truncate">{u.email}</p>}
                                    </div>
                                    {isSelected && <span className="text-indigo-400 text-xs font-bold">✓</span>}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Error */}
                {error && (
                    <p className="px-5 text-xs text-red-400 font-medium">{error}</p>
                )}

                {/* Actions */}
                <div className="flex gap-3 px-5 py-4 border-t border-white/5">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 text-xs font-bold uppercase tracking-widest transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedUser || isSubmitting}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
                            !selectedUser || isSubmitting
                                ? 'bg-indigo-500/30 text-indigo-300/50 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/20'
                        }`}
                    >
                        {isSubmitting ? 'Adding...' : 'Add Member'}
                    </button>
                </div>
            </div>
        </div>
    );
}
